'use client';

import { popupCenter } from '@/lib/helpers';
import {
  ContentWithSnippet,
  CreateResponse,
  ErrorResponse,
} from '@/types/sermons';
import { default as classNames, default as cx } from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import {
  BsBookmark,
  BsBookmarkFill,
  BsFileEarmarkText,
  BsPencilSquare,
  BsTrash,
  BsXCircle,
} from 'react-icons/bs';
import slugify from 'slugify';
import ContentDeleteDialog from '../admin/content/content-delete-dialog';
import ContentEditDialog from '../admin/content/content-edit-dialog';
import Button from '../molecules/button';
import LoggedOutMessage from '../molecules/logged-out-message';
import { useAppState } from '../providers/state-provider';

export default function SearchResult({
  result,
  index,
  allIds,
  type,
  hideScripture,
  isPopup,
  onBookmark,
  pericopeCode,
  term: parentTerm,
}: {
  result: ContentWithSnippet;
  index: number;
  allIds: string[];
  type: string;
  hideScripture?: boolean;
  isPopup?: boolean;
  onBookmark?: () => void;
  pericopeCode?: string;
  term?: string;
}) {
  const {
    state: {
      user,
      token,
      bookmarks,
      isLoading,
      mySermons,
      searchResults,
      isBrowser,
    },
    dispatch,
  } = useAppState();
  const { isMySermons } = mySermons;

  const [editingContent, setEditingContent] = useState(false);
  const [deletingContent, setDeletingContent] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [loading, setLoading] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();

  return (
    <>
      <article
        key={result.id}
        className={classNames({
          'cursor-pointer': type !== 'Illustrations · Quotes · Humor',
        })}
        onClick={(e) => {
          if (
            type !== 'Illustrations · Quotes · Humor' &&
            !(e.target as HTMLDivElement).closest('.action')
          ) {
            if (isMySermons && result.isDraft) {
              router.push(`/my-sermons?id=${result.id}`);
            } else {
              dispatch({
                type: 'searchResults',
                payload: {
                  ...searchResults,
                  ids: allIds,
                  resultsTerm: parentTerm || null,
                },
              });

              const linkElem = (
                e.currentTarget as HTMLDivElement
              ).querySelector('a');

              if (linkElem) {
                linkElem.click();
                if (!isPopup) {
                  scrollTo({ top: 0 });
                }
              }
            }
            e.preventDefault();
          }
        }}
      >
        <header className='mb-2 flex items-start justify-between'>
          <div>
            {result.contentType !== '39' && (
              <div className={cx('text-xl font-bold uppercase', {})}>
                {['2', '14'].includes(result.contentType) &&
                  `${index}. ${result.contentTitle}`}
                {!['2', '14'].includes(result.contentType) && (
                  <Link
                    className='cursor-pointer hover:underline'
                    href={`/sermon${isPopup ? '/popout' : ''}/${slugify(
                      result.contentTitle || '_',
                    ).toLowerCase()}/${result.id}${
                      pericopeCode ? `?pericopeCode=${pericopeCode}` : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isMySermons && result.isDraft) {
                        e.preventDefault();
                        router.push(`/my-sermons?id=${result.id}`);
                      } else {
                        dispatch({
                          type: 'searchResults',
                          payload: {
                            ...searchResults,
                            ids: allIds,
                            resultsTerm: parentTerm || null,
                          },
                        });
                        if (!isPopup) {
                          scrollTo({ top: 0 });
                        }
                      }
                    }}
                    scroll={!isPopup}
                  >
                    {index}. {result.contentTitle}
                  </Link>
                )}
                {user && !isLoading && !isMySermons && (
                  <>
                    {' '}
                    {bookmarks && bookmarks.includes(result.id) && (
                      <button
                        type='button'
                        className='action'
                        onClick={async () => {
                          if (loading) {
                            return;
                          }

                          setLoading(true);

                          const res = await fetch(
                            `${process.env.NEXT_PUBLIC_API_BASE_URL}/contents/bookmarked-sermons/${result.id}`,
                            {
                              method: 'DELETE',
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                              },
                              cache: 'no-store',
                              next: {
                                tags: ['all', 'contents', 'bookmarks'],
                              },
                            },
                          );

                          const contentsResponse = (await res.json()) as
                            | CreateResponse
                            | ErrorResponse;
                          if ('statusCode' in contentsResponse) {
                            setError(
                              Array.isArray(contentsResponse.message)
                                ? contentsResponse.message.join('\n')
                                : contentsResponse.message,
                            );
                            setTimeout(() => {
                              setError('');
                            }, 5000);
                          } else {
                            dispatch({
                              type: 'bookmarks',
                              payload: bookmarks.filter((b) => b !== result.id),
                            });
                            setSuccess(contentsResponse.success);
                            setTimeout(() => {
                              setSuccess('');
                            }, 5000);
                          }

                          if (onBookmark) {
                            onBookmark();
                          } else {
                            router.refresh();
                          }
                          setLoading(false);
                        }}
                      >
                        {loading && (
                          <Image
                            className='inline-block'
                            src={'/img/ajax-loader.gif'}
                            width={16}
                            height={11}
                            alt='Loading...'
                            unoptimized
                          />
                        )}
                        {!loading && (
                          <BsBookmarkFill className='inline-block' />
                        )}
                      </button>
                    )}
                    {!bookmarks ||
                      (!bookmarks.includes(result.id) && (
                        <button
                          type='button'
                          className='action'
                          onClick={async () => {
                            if (loading) {
                              return;
                            }

                            setLoading(true);

                            const res = await fetch(
                              `${process.env.NEXT_PUBLIC_API_BASE_URL}/contents/bookmarks`,
                              {
                                method: 'POST',
                                body: JSON.stringify({
                                  sermonId: result.id,
                                }),
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${token}`,
                                },
                                cache: 'no-store',
                                next: {
                                  tags: ['all', 'contents', 'bookmarks'],
                                },
                              },
                            );

                            const contentsResponse = (await res.json()) as
                              | CreateResponse
                              | ErrorResponse;
                            if ('statusCode' in contentsResponse) {
                              setError(
                                Array.isArray(contentsResponse.message)
                                  ? contentsResponse.message.join('\n')
                                  : contentsResponse.message,
                              );
                              setTimeout(() => {
                                setError('');
                              }, 5000);
                            } else {
                              dispatch({
                                type: 'bookmarks',
                                payload: [...bookmarks, result.id],
                              });
                              setSuccess(contentsResponse.success);
                              setTimeout(() => {
                                setSuccess('');
                              }, 5000);
                            }

                            if (onBookmark) {
                              onBookmark();
                            }

                            setLoading(false);
                          }}
                        >
                          {loading && (
                            <Image
                              className='inline-block'
                              src={'/img/ajax-loader.gif'}
                              width={16}
                              height={11}
                              alt='Loading...'
                              unoptimized
                            />
                          )}
                          {!loading && <BsBookmark className='inline-block' />}
                        </button>
                      ))}
                  </>
                )}
              </div>
            )}
            {!hideScripture && (
              <div className='text-sm font-bold text-sermons-dark'>
                {result.displayedScripture?.[0]?.displayedScripture ||
                  result.pericopes?.map((p) => p.fullReference).join(', ')}
              </div>
            )}
          </div>
          <div className='text-right'>
            {result.tbTocType?.tocName !== 'Quote' && (
              <>
                <div className='text-sm font-bold text-sermons-dark'>
                  {result.tbTocType?.tocName || ''}
                </div>
                <div className='text-sm font-bold text-sermons-dark'>
                  {`${result.authors?.[0]?.firstname || ''} ${
                    result.authors?.[0]?.lastname || ''
                  }`.trim() || ''}
                </div>
              </>
            )}
          </div>
        </header>
        <div className='float-right p-4 pr-0 pt-0'>
          {!isPopup &&
            !['Humor', 'Humor Illustration', 'Illustration', 'Quote'].includes(
              result.tbTocType?.tocName || '',
            ) && (
              <Link
                href={`/sermon/popout/${slugify(
                  result.contentTitle || '_',
                ).toLowerCase()}/${result.id}${
                  pericopeCode ? `?pericopeCode=${pericopeCode}` : ''
                }`}
                className='action mx-2 inline-flex size-10 items-center justify-center rounded align-top text-3xl'
                onClick={() =>
                  dispatch({
                    type: 'searchResults',
                    payload: {
                      ...searchResults,
                      ids: allIds,
                      resultsTerm: parentTerm || null,
                    },
                  })
                }
                scroll={false}
              >
                <Button className='action inline-flex size-10 items-center justify-center text-3xl'>
                  <svg
                    width={20}
                    height={20}
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 24 24'
                    stroke='#fff'
                    strokeWidth={1}
                  >
                    <defs>
                      <linearGradient
                        gradientUnits='userSpaceOnUse'
                        id='a'
                        x1='-0.54793'
                        x2='24.75657'
                        y1='5.87644'
                        y2='17.67613'
                      >
                        <stop offset='0' stopColor='#fff' stopOpacity='0.1' />
                        <stop offset='1' stopColor='#fff' stopOpacity='0' />
                      </linearGradient>
                    </defs>
                    <title />
                    <path
                      d='M22.5,11a.49971.49971,0,0,0-.5.5V21a1.001,1.001,0,0,1-1,1H3a1.001,1.001,0,0,1-1-1V3A1.001,1.001,0,0,1,3,2h9.5a.5.5,0,0,0,0-1H3A2.00229,2.00229,0,0,0,1,3V21a2.00229,2.00229,0,0,0,2,2H21a2.00229,2.00229,0,0,0,2-2V11.5A.49971.49971,0,0,0,22.5,11Z'
                      fill='#fff'
                    />
                    <path
                      d='M22.5,1h-7a.5.5,0,0,0,0,1h5.793L14.14648,9.14648a.5.5,0,1,0,.707.707L22,2.707V8.5a.5.5,0,0,0,1,0v-7A.49971.49971,0,0,0,22.5,1Z'
                      fill='#fff'
                    />
                    <path
                      d='M22.5,11a.49971.49971,0,0,0-.5.5V21a1.001,1.001,0,0,1-1,1H3a1.001,1.001,0,0,1-1-1V3A1.001,1.001,0,0,1,3,2h9.5a.5.5,0,0,0,0-1H3A2.00229,2.00229,0,0,0,1,3V21a2.00229,2.00229,0,0,0,2,2H21a2.00229,2.00229,0,0,0,2-2V11.5A.49971.49971,0,0,0,22.5,11Zm0-10h-7a.5.5,0,0,0,0,1h5.793L14.14648,9.14648a.5.5,0,1,0,.707.707L22,2.707V8.5a.5.5,0,0,0,1,0v-7A.49971.49971,0,0,0,22.5,1Z'
                      fill='url(#a)'
                    />
                  </svg>
                </Button>
              </Link>
            )}
          {['Humor', 'Humor Illustration', 'Illustration'].includes(
            result.tbTocType?.tocName || '',
          ) && (
            <>
              <a
                href={`/sermon${isPopup ? '/popout' : ''}/${slugify(
                  result.contentTitle || '_',
                ).toLowerCase()}/${result.id}/print${
                  pericopeCode ? `?pericopeCode=${pericopeCode}` : ''
                }`}
                target='_blank'
                rel='noopener noreferrer'
                className='action mr-2 inline-flex size-10 items-center justify-center rounded text-3xl'
                onClick={(e) => {
                  e.preventDefault();
                  popupCenter({
                    url: `/sermon/${slugify(
                      result.contentTitle || '_',
                    ).toLowerCase()}/${result.id}/print${
                      pericopeCode ? `?pericopeCode=${pericopeCode}` : ''
                    }`,
                    title: '_blank',
                    w: 600,
                    h: 800,
                  });
                }}
              >
                <Button className='action inline-flex size-10 items-center justify-center text-3xl'>
                  <BsFileEarmarkText />
                </Button>
              </a>
              <a
                href={`/sermon${isPopup ? '/popout' : ''}/${slugify(
                  result.contentTitle || '_',
                ).toLowerCase()}/${result.id}/print?print=true${
                  pericopeCode ? `&pericopeCode=${pericopeCode}` : ''
                }`}
                target='_blank'
                rel='noopener noreferrer'
                className='action mr-2 inline-flex size-10 items-center justify-center rounded align-top text-3xl'
                onClick={(e) => {
                  e.preventDefault();
                  popupCenter({
                    url: `/sermon/${slugify(
                      result.contentTitle || '_',
                    ).toLowerCase()}/${result.id}/print?print=true${
                      pericopeCode ? `&pericopeCode=${pericopeCode}` : ''
                    }`,
                    title: '_blank',
                    w: 600,
                    h: 800,
                  });
                }}
              >
                <Button className='action inline-flex size-10 items-center justify-center text-3xl'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    enableBackground='new 0 0 512 512'
                    height={20}
                    id='Layer_1'
                    version='1.1'
                    viewBox='0 0 512 512'
                    width={20}
                  >
                    <g>
                      <path
                        d='M484.281,210.609l-28.906-33.031C445.594,166.406,431.469,160,416.625,160H384V56c0-22.094-17.906-40-40-40   H237.656c-8.75,0-17.141,3.469-23.313,9.656l-76.688,76.688c-6.188,6.188-9.656,14.563-9.656,23.313V160h-32.64   c-14.844,0-28.969,6.406-38.734,17.578l-28.922,33.031C20.156,219.25,16,230.313,16,241.781V352c0,35.344,28.656,64,64,64h27.344   l-9.875,29.625c-2.656,7.969-1.313,16.719,3.594,23.531C105.97,475.968,113.844,480,122.25,480h267.594   c8.375,0,16.25-4.016,21.156-10.813c4.922-6.798,6.281-15.531,3.656-23.484L404.844,416H432c35.344,0,64-28.656,64-64V241.781   C496,230.313,491.828,219.25,484.281,210.609z M224,38.625V88c0,13.234-10.781,24-24,24h-49.375L224,38.625z M144,128h56   c22.094,0,40-17.906,40-40V32h104c13.219,0,24,10.766,24,24v136H144V128z M39.75,221.141l28.906-33.031   C75.391,180.422,85.125,176,95.359,176H128v16h-8c-4.438,0-8,3.578-8,8s3.563,8,8,8h272c4.406,0,8-3.578,8-8s-3.594-8-8-8h-8v-16   h32.625c10.234,0,19.969,4.422,26.703,12.109l28.922,33.031c0.781,0.891,1.406,1.891,2.063,2.859H37.672   C38.344,223.031,38.969,222.031,39.75,221.141z M96,400v-32c0-17.656,14.344-32,32-32h6l-21.344,64H96z M398.063,459.813   c-1.875,2.625-4.938,4.188-8.188,4.188h-267.75c-3.25,0-6.313-1.563-8.219-4.188c-1.875-2.625-2.406-6.03-1.406-9.094L150.5,336   H362l37.5,114.719C400.5,453.781,399.969,457.188,398.063,459.813z M416,400h-16.422l-21.109-64H384c17.656,0,32,14.344,32,32V400z    M480,352c0,26.469-21.531,48-48,48v-32c0-26.516-21.5-48-48-48H128c-26.516,0-48,21.484-48,48v32c-26.469,0-48-21.531-48-48   V241.781c0-0.594,0.125-1.188,0.156-1.781h447.672c0.047,0.594,0.172,1.188,0.172,1.781V352z M352,424c0,4.422-3.594,8-8,8H168   c-4.438,0-8-3.578-8-8s3.563-8,8-8h176C348.406,416,352,419.578,352,424z M167.984,392c0-4.422,3.578-8,8-8h161.609   c4.438,0,8,3.578,8,8s-3.563,8-8,8H175.984C171.563,400,167.984,396.422,167.984,392z M448,264c0,4.422-3.594,8-8,8h-32   c-4.438,0-8-3.578-8-8s3.563-8,8-8h32C444.406,256,448,259.578,448,264z M448,296c0,4.422-3.594,8-8,8h-32c-4.438,0-8-3.578-8-8   s3.563-8,8-8h32C444.406,288,448,291.578,448,296z M176,360c0-4.422,3.563-8,8-8h144c4.406,0,8,3.578,8,8s-3.594,8-8,8H184   C179.563,368,176,364.422,176,360z'
                        fill='#FFF'
                      />
                    </g>
                  </svg>
                </Button>
              </a>
            </>
          )}
          {user && (user.isAdmin || isMySermons) && (
            <>
              <Button
                className='action mr-2 inline-flex size-10 items-center justify-center text-3xl'
                onClick={(e) => {
                  e.preventDefault();
                  if (isMySermons) {
                    router.push(`/my-sermons?id=${result.id}`);
                  } else {
                    setEditingContent(true);
                  }
                }}
              >
                <BsPencilSquare />
              </Button>
              <Button
                className='action inline-flex size-10 items-center justify-center text-3xl'
                onClick={(e) => {
                  e.preventDefault();
                  setDeletingContent(true);
                }}
              >
                <BsTrash />
              </Button>
            </>
          )}
        </div>
        {result.tbTocType?.tocName !== 'Quote' && (
          <section
            className={classNames({
              'sermon-content': ![18, 21, 23, 24, 26, 33, 34, 35, 36].includes(
                +result.contentType,
              ),
            })}
            dangerouslySetInnerHTML={{ __html: result.snippet }}
          ></section>
        )}
        {result.tbTocType?.tocName === 'Quote' && (
          <div className='grid grid-cols-[auto_min-content] gap-4'>
            <section
              className={classNames({
                'sermon-content': ![
                  18, 21, 23, 24, 26, 33, 34, 35, 36,
                ].includes(+result.contentType),
              })}
              dangerouslySetInnerHTML={{ __html: result.snippet }}
            ></section>
            <div className='whitespace-nowrap text-sm font-bold text-sermons-dark'>
              <Link
                href={`/search/keyword?term=${encodeURIComponent(
                  `${result.authors?.[0]?.firstname || ''} ${
                    result.authors?.[0]?.lastname || ''
                  }`.trim() || '',
                )}&type=${encodeURIComponent(type)}&tab=${encodeURIComponent(
                  searchParams.get('tab') || '',
                )}`}
              >
                {`${result.authors?.[0]?.firstname || ''} ${
                  result.authors?.[0]?.lastname || ''
                }`.trim() || ''}
              </Link>
            </div>
          </div>
        )}
        {type === 'Illustrations · Quotes · Humor' &&
          (!user || !user.hasIllustrations || user.isExpired) &&
          !isLoading &&
          isBrowser && (
            <>
              <div className='clear-both'></div>
              <LoggedOutMessage />
            </>
          )}
      </article>
      <ContentEditDialog
        sermon={result}
        isOpen={editingContent}
        setOpen={setEditingContent}
        setError={setError}
        setSuccess={setSuccess}
        onSave={() => router.refresh()}
      />
      <ContentDeleteDialog
        sermon={result}
        isOpen={deletingContent}
        setOpen={setDeletingContent}
        onDelete={() => router.refresh()}
      />
      <section
        className={cx('fixed bottom-0 left-0 z-50 w-full text-white', {
          hidden: !error && !success,
        })}
      >
        {error && (
          <div className='m-4 mx-auto flex items-center justify-between rounded bg-red-400 p-4 lg:max-w-6xl'>
            <div className='whitespace-pre-wrap'>{error}</div>
            <div className='flex w-4 items-center text-right text-2xl'>
              <button onClick={() => setError('')}>
                <BsXCircle />
              </button>
            </div>
          </div>
        )}
        {success && (
          <div className='m-4 mx-auto flex items-center justify-between rounded bg-green-400 p-4 lg:max-w-6xl'>
            <div className='whitespace-pre-wrap'>{success}</div>
            <div className='flex w-4 items-center text-right text-2xl'>
              <button onClick={() => setSuccess('')}>
                <BsXCircle />
              </button>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
